import { supabase } from '@/integrations/supabase/client';
import { adminApi } from '@/lib/admin/adminApi';
import { getDomain } from '@/config/domains';
import type { Session, User, AuthChangeEvent } from '@supabase/supabase-js';

export async function signOutRpc(): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('[Auth Facade] Sign out failed:', error);
      return { ok: false, error: error.message };
    }
    return { ok: true };
  } catch (error) {
    console.error('[Auth Facade] Sign out error:', error);
    return { ok: false, error: 'Sign out failed' };
  }
}

// Legacy signOut function for compatibility
export async function signOut(): Promise<{ ok: boolean; error?: string }> {
  return signOutRpc();
}

export async function getCurrentSession(): Promise<Session | null> {
  try {
    // E2E Testing Bypass
    if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_admin_bypass') === 'true') {
      const email = window.localStorage.getItem('e2e_user_email') || 'admin@test.com';
      const userId = 'test-admin-user-id';

      // Return a mock session that matches Supabase Session type
      return {
        access_token: 'mock-e2e-access-token',
        refresh_token: 'mock-e2e-refresh-token',
        expires_in: 3600,
        expires_at: Math.floor(Date.now() / 1000) + 3600,
        token_type: 'bearer',
        user: {
          id: userId,
          email,
          aud: 'authenticated',
          role: 'authenticated',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          app_metadata: {},
          user_metadata: { e2e_test_user: true },
        },
      } as Session;
    }

    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('[Auth Facade] Get session failed:', error);
      return null;
    }
    return data.session;
  } catch (error) {
    console.error('[Auth Facade] Get session error:', error);
    return null;
  }
}

// Alternative function that returns error info
export async function getCurrentSessionWithError() {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('[Auth Facade] Get session failed:', error);
      return { session: null, error: error.message };
    }
    return { session: data.session, error: null };
  } catch (error) {
    console.error('[Auth Facade] Get session error:', error);
    return { session: null, error: 'Failed to get session' };
  }
}

// Get user ID from session
export function getUserId(session?: Session | null): string | null {
  return session?.user?.id || null;
}

// Get current user
export async function getCurrentUser(): Promise<User | null> {
  const session = await getCurrentSession();
  return session?.user || null;
}

// Auth state bridge - replaces direct Supabase client usage in UI
export function onAuthStateChange(cb: (s: Session | null) => void): () => void {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (event, session) => cb(session)
  );
  return () => subscription.unsubscribe();
}

// Code exchange for auth callback
export async function exchangeCodeForSession(url: string): Promise<void> {
  try {
    await supabase.auth.exchangeCodeForSession(url);
    return;
  } catch {
    /* try alternative code extraction */
  }

  const code = new URL(url).searchParams.get('code');
  if (code) await supabase.auth.exchangeCodeForSession(code);
}

// Setup auth listener
export function setupAuthListener(callback: (event: AuthChangeEvent, session: Session | null) => void) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
  return () => subscription.unsubscribe();
}

// Magic Link types and functions
export type MagicLinkResult = {
  success: true;
  error: null;
  data: { user: any|null; session: any|null; request_id: string };
} | {
  success: false;
  error: { code?: string; message?: string } | Error;
  data?: undefined;
};

// Provider mode cache for performance (5 minutes TTL)
const providerModeCache: { mode: 'resend'|'supabase'|'auto'; expires: number } | null = null;

export async function sendMagicLink(options: {
  email: string;
  redirect_to?: string;
  create_user?: boolean;
  surface?: 'public' | 'admin'
}): Promise<MagicLinkResult> {
  const { email } = options;
  const surface = options.surface ?? 'public';
  const redirect_to = options.redirect_to ?? `${getDomain(surface)}/auth/callback`;

  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem('pending_email', email);
    } catch {
      /* localStorage not available */
    }
  }

  if (!email || !email.includes('@')) {
    return { success: false, error: { code: 'invalid_email', message: 'Invalid email' } };
  }

  try {
    // Use the new unified magic link broker
    const { data, error } = await supabase.functions.invoke('auth-magic-link-broker', {
      body: {
        email,
        redirect_to,
        create_user: options.create_user !== false
      }
    });

    if (error) {
      throw new Error(error.message || 'Magic link send failed');
    }

    const result = data;

    // Optional: audit log (только если уже есть сессия)
    try {
      const session = await getCurrentSession();
      if (session?.access_token) {
        const { callSingle } = await import('@/lib/rpc/facade');
        const res = await callSingle('admin_audit_log_insert', [
          'magiclink_sent', 'info', true, null, {
            provider: result.provider,
            failover_from: result.failover_from,
            email: email.substring(0, 3) + '***', // Privacy-safe logging
            idempotent: result.idempotent || false
          }
        ]);
        if (!res.ok) {
          console.debug('[sendMagicLink] Audit log failed:', (res as any).error || (res as any).code);
        }
      } else {
        console.debug('[sendMagicLink] Skip audit log: no session yet');
      }
    } catch (auditError) {
      console.debug('[sendMagicLink] Audit log error:', auditError);
    }
    
    return {
      success: true,
      error: null,
      data: { 
        user: null, 
        session: null, 
        request_id: result.provider || 'magic-link-sent'
      }
    };
  } catch (error) {
    console.error('[sendMagicLink] Error:', error);
    return { 
      success: false, 
      error: { code: 'send_failed', message: String(error) } 
    };
  }
}

// Alias for compatibility
export const authMagicLink = sendMagicLink;